import { Link } from 'react-router-dom';
import {
    faArrowRight,
    faCheckCircle,
    faClock,
    faDoNotEnter,
    faExclamationTriangle,
    faSync,
} from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';

const TransactionIntegrations = () => {
    const { accountId } = useCurrentAccount();
    const { data: transaction } = useCurrentTransactionOrById();

    return (
        <Collapsible.Root defaultOpen={true}>
            <Collapsible.Trigger
                className={
                    'group flex w-full items-center justify-between gap-2 rounded-md bg-gray-100 px-3 py-2'
                }
            >
                <p className={'text-sm font-medium'}>Integrations</p>
                <FontAwesomeIcon
                    className={'group-radix-state-open:rotate-180'}
                    icon={faChevronDown}
                />
            </Collapsible.Trigger>
            <Collapsible.Content className={'flex flex-col gap-6 px-3 py-4'}>
                <div className="flex flex-col gap-1">
                    <p className="flex justify-between text-sm font-medium text-gray-800">
                        QuickBooks Sync Status
                        <Link
                            to={`/accounts/${accountId}/settings/integrations/quickbooks`}
                            className="text-gray-500"
                            aria-label="Go to QuickBooks integration"
                        >
                            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                        </Link>
                    </p>
                    {transaction.integrations?.quickbooks.mapping?.status && (
                        <TransactionSyncStatus />
                    )}
                    <TransactionSyncInfo />
                </div>
            </Collapsible.Content>
        </Collapsible.Root>
    );
};

const TransactionSyncStatus = () => {
    const { data: transaction } = useCurrentTransactionOrById();
    const status = transaction.integrations?.quickbooks.mapping?.status;

    if (!status) {
        return null;
    }

    const qbStatus = {
        synced: (
            <div className="flex items-center gap-2 text-gray-500">
                Synced <FontAwesomeIcon icon={faCheckCircle} />
            </div>
        ),
        do_not_sync: (
            <div className="flex items-center gap-2 text-gray-500">
                Do Not Sync <FontAwesomeIcon icon={faDoNotEnter} />
            </div>
        ),
        issues: (
            <div className="text-warning-700 flex items-center gap-2">
                Sync Issue <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
        ),
        before_historical_date: (
            <div className="flex items-center gap-2 text-gray-500">
                Before Historical Date <FontAwesomeIcon icon={faClock} />
            </div>
        ),
        syncing: (
            <div className="flex items-center gap-2 text-gray-500">
                Syncing <FontAwesomeIcon icon={faSync} />
            </div>
        ),
        ready_to_sync: (
            <div className="text-success-700 flex items-center gap-2">
                Ready To Sync <FontAwesomeIcon icon={faSync} />
            </div>
        ),
    }[status];

    return qbStatus;
};

const TransactionSyncInfo = () => {
    const { data: transaction } = useCurrentTransactionOrById();
    const status = transaction.integrations?.quickbooks.mapping?.status;

    if (status === 'synced' && transaction.integrations?.quickbooks.mapping?.synced_at) {
        return (
            <span className="text-sm text-gray-600">
                {isoDateFromServer(
                    transaction.integrations.quickbooks.mapping.synced_at,
                ).toLocaleString(DateTime.DATE_MED)}
            </span>
        );
    }

    if (status === 'do_not_sync' && transaction.integrations?.quickbooks.mapping?.added_at) {
        return (
            <span className="text-sm text-gray-600">
                {isoDateFromServer(
                    transaction.integrations.quickbooks.mapping.added_at,
                ).toLocaleString(DateTime.DATE_MED)}
            </span>
        );
    }

    if (status === 'issues' && transaction.integrations?.quickbooks.mapping?.latest_issue_message) {
        return (
            <span className="text-sm text-gray-600">
                Mapping issue: {transaction.integrations.quickbooks.mapping.latest_issue_message}
            </span>
        );
    }

    return null;
};

export { TransactionIntegrations };
